"use client";

import Link from "next/link";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "@/lib/i18n/context";

export function LegalShell({
  title,
  markdown,
  children,
}: {
  title: string;
  markdown?: string;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const home = "/";
  // Legal pages are English-only — links are constant regardless of UI language.
  const privacyHref = "/privacy";
  const cookiesHref = "/cookies";
  const termsHref = "/terms";
  const aiHref = "/ai";
  const gdprHref = "/gdpr";

  return (
    <main className="mx-auto min-h-screen max-w-3xl px-6 py-12 text-[var(--ink)]">
      <header className="mb-10 border-b border-[var(--rule)] pb-5">
        <Link href={home} className="font-display text-[20px] italic text-[var(--terracotta)] hover:underline">
          Journail
        </Link>
        <h1 className="mt-3 font-display text-[34px] leading-[1.1] tracking-[-0.015em]">{title}</h1>
      </header>
      <article className="max-w-none text-[15px] leading-[1.7] text-[var(--ink)] [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:font-display [&_h2]:text-[22px] [&_h2]:text-[var(--ink)] [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:font-mono [&_h3]:text-[12px] [&_h3]:uppercase [&_h3]:tracking-[0.18em] [&_h3]:text-[var(--ink-soft)] [&_p]:my-3 [&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1 [&_li::marker]:text-[var(--ink-soft)] [&_a]:text-[var(--terracotta)] [&_a]:underline-offset-2 [&_a:hover]:underline [&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[13.5px] [&_table]:border [&_table]:border-[var(--rule)] [&_thead]:bg-[var(--paper-2)] [&_th]:border-b [&_th]:border-[var(--rule)] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium [&_th]:text-[var(--ink)] [&_td]:border-b [&_td]:border-[var(--rule)]/60 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:text-[var(--ink-soft)] [&_code]:rounded [&_code]:bg-[var(--paper-2)] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px] [&_code]:text-[var(--ink)] [&_strong]:font-semibold [&_strong]:text-[var(--ink)] [&_hr]:my-7 [&_hr]:border-0 [&_hr]:h-px [&_hr]:bg-[var(--rule)]">
        {markdown ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown> : children}
      </article>
      <nav className="mt-12 flex flex-wrap gap-4 border-t border-[var(--rule)] pt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
        <Link href={privacyHref} className="hover:text-[var(--terracotta)]">{t.consent.footer.privacy}</Link>
        <Link href={cookiesHref} className="hover:text-[var(--terracotta)]">{t.consent.footer.cookies}</Link>
        <Link href={termsHref} className="hover:text-[var(--terracotta)]">{t.consent.footer.terms}</Link>
        <Link href={aiHref} className="hover:text-[var(--terracotta)]">AI</Link>
        <Link href={gdprHref} className="hover:text-[var(--terracotta)]">GDPR</Link>
        <button
          type="button"
          onClick={() => typeof window !== "undefined" && window.openCookiePreferences?.()}
          className="hover:text-[var(--terracotta)] uppercase"
        >
          {t.consent.footer.preferences}
        </button>
      </nav>
    </main>
  );
}
